import "./ConstructorGrid.style.css";

import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { ObjectSettings } from "../ObjectSettings/ObjectSettings";
import { ObjectState } from "../../../utils/states/ConstructorPageStates/ObjectState";
import { PropertyState } from "../../../utils/states/ConstructorPageStates/PropertyState";
import { UniqueId } from "../../../utils/UniqueId";
import { getDefaultValueForType } from "../../../utils/functions/getDefaultValueForType";
import { Status } from "../../../utils/enums/Status";
import { ObjectValue } from "../../../utils/states/ConstructorPageStates/ObjectValue";
import { DEFAULT_OBJECT_IMAGE } from "../../../utils/constants/constants";

interface ConstructorGridProps {
  objects: ObjectState[];
  properties: PropertyState[];
  setObjects: Dispatch<SetStateAction<ObjectState[]>>;
}

export const ConstructorGrid = ({
  objects,
  properties,
  setObjects,
}: ConstructorGridProps) => {
  const [editedObject, setEditedObject] = useState<ObjectState | null>(null);

  const objectRef = useRef<HTMLDivElement | null>(null);
  const objectSettingsRef = useRef<HTMLDivElement | null>(null);
  const addObjectRef = useRef<HTMLButtonElement | null>(null);

  const handleObjectClicked = (objectState: ObjectState) => {
    setEditedObject(objectState);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleClickOutside = (e: MouseEvent) => {
    const isEditedObject =
      objectRef.current && objectRef.current.contains(e.target as Node);
    const isAddedObject =
      addObjectRef.current && addObjectRef.current.contains(e.target as Node);
    const isObjectSettingsObject =
      objectSettingsRef.current &&
      objectSettingsRef.current.contains(e.target as Node);

    if (!isEditedObject && !isAddedObject && !isObjectSettingsObject) {
      setEditedObject(null);
    }
  };

  const handleAddObjectClicked = () => {
    const newObject: ObjectState = {
      id: UniqueId.generateUniqueId(),
      apiId: null,
      image: DEFAULT_OBJECT_IMAGE,
      name: "Новый объект",
      status: Status.Create,
      properties: properties.reduce((acc, property) => {
        acc[property.id] = {
          type: property.type,
          value: getDefaultValueForType(property.type),
        };
        return acc;
      }, {} as { [propertyId: number]: ObjectValue }),
    };

    setObjects((prevState) => [...prevState, newObject]);

    setEditedObject(newObject);
  };

  const handleChangeObject = (newObject: ObjectState) => {
    if (editedObject) {
      setObjects((prevObjects) =>
        prevObjects.map((obj) =>
          obj.id === editedObject?.id ? newObject : obj
        )
      );

      setEditedObject(newObject);
    }
  };

  const handleChangeObjectValue = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const [objectId, propertyId] = name.split("_").map((id) => parseInt(id));

    setObjects((prevObjects) => {
      return prevObjects.map((object) => {
        if (object.id === objectId) {
          const objectValue = object.properties[propertyId];

          return {
            ...object,
            status: object.apiId ? Status.Update : object.status,
            properties: {
              ...object.properties,
              [propertyId]: { type: objectValue.type, value: value },
            },
          };
        }

        return object;
      });
    });

    setEditedObject((prevState) => {
      if (!prevState) {
        return null;
      }

      const objectValue = prevState.properties[propertyId];

      return {
        ...prevState,
        status: prevState.apiId ? Status.Update : prevState.status,
        properties: {
          ...prevState.properties,
          [propertyId]: { type: objectValue.type, value: value },
        },
      };
    });
  };

  return (
    <div className="constructor_grid_container">
      <div className="constructor_grid__object_grid_container">
        <div
          className="constructor_grid__object_grid"
          style={{
            gridTemplateColumns: `repeat(${
              objects.length > 0
                ? objects.length >= 10
                  ? 10
                  : objects.length
                : 1
            }, 1fr)`,
          }}
        >
          {objects.map((object) => (
            <div
              className={
                object.id === editedObject?.id
                  ? "constructor_grid__object edited game_header"
                  : "constructor_grid__object game_header"
              }
              ref={(el) => {
                if (editedObject?.id === object.id) {
                  objectRef.current = el;
                }
              }}
              key={object.id}
              onClick={() => handleObjectClicked(object)}
            >
              <div
                className={
                  object.id === editedObject?.id
                    ? "constructor_grid__object_name_value editedImage"
                    : "constructor_grid__object_name_value"
                }
              >
                {object.name}
              </div>
              <img
                className="constructor_grid__object_name_image"
                src={object.image}
                alt="object_image"
              />
            </div>
          ))}
        </div>
        <button
          ref={addObjectRef}
          onClick={handleAddObjectClicked}
          className="constructor_page__object_add_button header_4"
        >
          Добавить объект
        </button>
      </div>

      {editedObject && (
        <ObjectSettings
          objectSettingsRef={objectSettingsRef}
          setObjects={setObjects}
          setEditedObject={setEditedObject}
          handleChangeObject={handleChangeObject}
          editedObject={editedObject}
          properties={properties}
          handleValueChange={handleChangeObjectValue}
        />
      )}
    </div>
  );
};
